import * as React from "react"
import { CheckIcon, Spacer } from "gatsby-interface"
import { Link } from "@gatsbyjs/reach-router"
import { CrossIcon } from "gatsby-ui/src/components/icons"
import { Spinner } from "gatsby-ui/src/components"
import { MerlinSourceEvent, MerlinStatus } from "@gatsbyjs/graphql/types"
import { getContextuallyFormattedDate } from "./helpers"
import * as styles from "./styles.css"
import { FiChevronRight, FiSlash } from "react-icons/fi"
import {
  SupportedCmsVendor,
  dataSourceNames,
  dataSourceLogos,
} from "../../constants/data-sources"
import { useMerlinSourceEventChangedSubscription } from "./queries.generated"
import { MdPending } from "react-icons/md"
import { isSystemVendor } from "../../utils/is-system-vendor"

export const statusIcons = {
  [MerlinStatus.Cancelled]: <FiSlash className={styles.cancelColor} />,
  [MerlinStatus.Success]: <CheckIcon className={styles.successColor} />,
  [MerlinStatus.Pending]: <MdPending />,
  [MerlinStatus.Idle]: null,
  [MerlinStatus.InProgress]: <Spinner />,
  [MerlinStatus.Failed]: <CrossIcon className={styles.failedColor} />,
}

type StatusIconProps = {
  status: MerlinStatus
}

function StatusIcon({ status }: StatusIconProps) {
  const icon = statusIcons?.[status]

  return icon || null
}

type SourceIconProps = {
  source: SupportedCmsVendor
}

function SourceIcon({ source }: SourceIconProps) {
  const Icon = dataSourceLogos?.[source]

  if (Icon) {
    return <Icon className={styles.eventSourceIcon} />
  }

  return null
}

export type EventProps = {
  event: MerlinSourceEvent
}

export function Event(props: EventProps) {
  const { data } = useMerlinSourceEventChangedSubscription({
    variables: {
      sourceEventId: props.event.id,
    },
    skip: props.event.status === MerlinStatus.Success,
  })
  const event = Object.assign({}, props.event, data?.merlinSourceEventChanged)
  const isFailed = event.status === MerlinStatus.Failed
  const cmsVendor = event.cmsVendor as SupportedCmsVendor
  const knownSource = dataSourceNames?.[cmsVendor]
  const name = knownSource || `Unknown source`

  const isSystemEvent = isSystemVendor(event.cmsVendor)
  const baseLinkUrl = `source-event/${event.id}`
  const linkUrl = isSystemEvent ? `${baseLinkUrl}/raw` : baseLinkUrl

  return (
    <div
      className={`${styles.event} ${isFailed ? styles.failedBackground : ``}`}
    >
      <div className={styles.eventStatus}>
        <StatusIcon status={event.status} />
      </div>
      <div className={styles.eventCreatedAt}>
        {getContextuallyFormattedDate(event.createdAt)}
      </div>

      <div className={styles.eventDetails}>
        <Link to={linkUrl} className={styles.eventDetailsLink}>
          {knownSource && (
            <>
              <SourceIcon source={cmsVendor} />
              <Spacer direction="horizontal" size={3} />
            </>
          )}
          <span className={styles.eventSourceName}>
            {name} <FiChevronRight className={styles.linkSign} />
          </span>
        </Link>
      </div>

      <div>
        {isFailed && (
          <span className={styles.eventErrorInfo}>
            {isSystemEvent ? `Failed` : `Failed to source data`}
          </span>
        )}
      </div>
    </div>
  )
}
